<script setup>
import { computed, useAttrs, ref } from 'vue';
import { useField } from 'vee-validate';

const props = defineProps({
  initialValue: {
    type: [String, Number, null],
    required: false,
    default: null,
  },
  name: {
    type: String,
    required: true
  },

  label: {
    type: String,
    required: true
  },

  description: {
    type: String,
    required: false,
    default: null
  },

  placeholder: {
    type: String,
    required: false,
    default: null
  },

  validatorName: {
    type: String,
    required: false,
    default: null,
  },

  rules: {
    type: String,
    required: false,
    default: null
  },

  mode: {
    type: String,
    required: false,
    default: 'eager'
  },

  type: {
    type: String,
    required: false,
    default: 'text'
  },

  disabled: {
    type: Boolean,
    required: false,
    default: false
  },

  showRequiredIndicator: {
    type: Boolean,
    required: false,
    default: true
  },

  autocomplete: {
    type: String,
    required: false,
    default: ''
  },

  autocapitalize: {
    type: String,
    required: false,
    default: ''
  }
});

const useFieldOpts = {
  label: props.validatorName ?? props.label,
  validateOnValueUpdate: false,
};

if (props.initialValue) {
  useFieldOpts.initialValue = props.initialValue;
}

const {
  errorMessage,
  handleChange,
  value,
  validate: validateField,
} = useField(props.name, props.rules, useFieldOpts);

const validationListeners = computed(() => {
  if (!errorMessage.value) {
    return {
      blur: handleChange,
      change: handleChange,
      input: (e) => handleChange(e, false),
    };
  }

  return {
    blur: handleChange,
    change: handleChange,
    input: handleChange,
  };
});
const activeInputType = ref(props.type);

const isRequired = computed(() => props.rules && props.rules.indexOf('required') !== -1);

function togglePasswordVisibility() {
  if (activeInputType.value === 'password') {
    activeInputType.value = 'text';
  } else {
    activeInputType.value = 'password';
  }
}

async function validate() {
  return {
    ...await validateField(),
    value: value.value,
  };
}

defineExpose({ validate });

defineOptions({
  inheritAttrs: false
});

const attrs = useAttrs();

const listeners = computed(() => {
  if (attrs) {
    return Object.fromEntries(
      Object
        .keys(attrs)
        .filter((key) => key.startsWith('on'))
        .map((key) => [key, attrs[key]])
    );
  }

  return null;
});

const nonListenerAttrs = computed(() => {
  if (attrs) {
    return Object.fromEntries(
      Object
        .keys(attrs)
        .filter((key) => !key.startsWith('on'))
        .map((key) => [key, attrs[key]])
    );
  }

  return null;
});
</script>

<template>
  <div
    class="ce-field"
    v-bind="nonListenerAttrs">
    <label
      v-if="label"
      :for="name">
      {{ label }} <span v-if="isRequired && showRequiredIndicator">*</span>
      <span
        v-if="errorMessage"
        class="ms-2 ml1 regular ce-colorError">
        {{ errorMessage }}
      </span>
    </label>

    <div
      v-if="description"
      class="ce-field-description">
      {{ description }}
    </div>

    <div
      class="ce-field-inputWrapper"
      :class="{ invalid: !!errorMessage }">
      <input
        v-if="type !== 'textarea'"
        ref="input"
        :id="name"
        :type="activeInputType"
        :name="name"
        v-model="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :autocapitalize="autocapitalize"
        @input="$emit('input', $event)"
        v-bind="listeners"
        v-on="validationListeners">

      <i
        class="ce-field-passwordToggle"
        v-if="type === 'password'"
        @click.prevent="togglePasswordVisibility">
        <fa-icon
          v-if="activeInputType === 'password'"
          :icon="['fas', 'eye']" />
        <fa-icon
          v-else
          :icon="['fas', 'eye-slash']" />
      </i>

      <textarea
        v-if="type === 'textarea'"
        ref="input"
        :id="name"
        :name="name"
        v-model="value"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="$emit('input', $event)"
        v-bind="listeners"
        v-on="validationListeners" />
    </div>
  </div>
</template>
